import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Compass, Linkedin, Mail, Phone, TrendingUp, Users, Landmark } from "lucide-react";
import { Eyebrow, MaskedLine, Reveal } from "@/components/Reveal";

const STATS = [
  ["10 Years", "As President & CEO"],
  ["$750M+", "Revenue growth"],
  ["20+", "Strategic acquisitions"],
  ["2026", "NorthStar Award recipient"],
];

const ADVISORY_SERVICES = [
  {
    icon: TrendingUp,
    title: "Business Growth Advisory",
    text: "Support for companies navigating expansion, acquisitions, revenue growth, market positioning, and operational scaling.",
  },
  {
    icon: Users,
    title: "Executive Coaching",
    text: "One-on-one advisory for founders, CEOs, senior leaders, and emerging executives focused on leadership, decision-making, and accountability.",
  },
  {
    icon: Landmark,
    title: "Board Governance Support",
    text: "Strategic insight for boards, advisory committees, and leadership teams requiring practical governance and business oversight experience.",
  },
];

const PROCESS = [
  { num: "01", title: "Discovery", text: "Understand the business, leadership priorities, challenges, and desired outcomes." },
  { num: "02", title: "Strategic review", text: "Assess current structure, opportunities, risks, leadership gaps, and growth paths." },
  { num: "03", title: "Action plan", text: "Create clear recommendations, priorities, timelines, and implementation steps." },
  { num: "04", title: "Ongoing advisory", text: "Provide continued executive support, accountability, and strategic guidance." },
];

const IDEAL_FOR = [
  { title: "Business owners", text: "Owners preparing for growth, transition, succession, acquisition, or operational change." },
  { title: "Executive teams", text: "Leadership teams seeking alignment, stronger execution, and outside strategic perspective." },
  { title: "Boards & advisory groups", text: "Boards requiring experienced business insight, governance perspective, and leadership support." },
];

const RECOGNITION = [
  "CEO of the Year — Ottawa Chamber of Commerce & Ottawa Business Journal",
  "EY Entrepreneur of the Year",
  "Canada's Defence Executive of the Year",
  "2026 NorthStar Award — Canadian business leadership",
];

export default function BusinessAdvisory() {
  useEffect(() => {
    document.title = "Business Advisory & Executive Leadership | Ford & Associates";
    return () => { document.title = "Ford & Associates | Accounting, Tax & Advisory — Newmarket, Ontario"; };
  }, []);

  return (
    <div className="bg-white pb-24" data-testid="business-advisory-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#FFFFFF] pt-[72px]" data-testid="ba-hero">
        <div className="pointer-events-none absolute -right-32 top-24 h-[460px] w-[460px] rounded-full bg-[#3682AE]/[0.07] blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-[1320px] grid-cols-1 items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:py-24">
          <div className="lg:col-span-7">
            <Eyebrow>Business Advisory &amp; Executive Leadership</Eyebrow>
            <h1 className="mt-5 font-display text-4xl font-medium leading-[1.05] tracking-tight text-[#102036] sm:text-6xl">
              <MaskedLine delay={0.15}>Executive advisory for</MaskedLine>
              <MaskedLine delay={0.3}>growth, leadership &amp;</MaskedLine>
              <MaskedLine delay={0.45}><em>business transition.</em></MaskedLine>
            </h1>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-[#4A5568] sm:text-lg">
              An advisory service for business owners, executives, boards, and leadership teams who need
              practical guidance, strategic clarity, and experienced support through growth, change,
              succession, and transformation.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                href="mailto:kevin@fordassociates.ca?subject=Website%20Contact"
                data-testid="ba-email-button"
                className="group inline-flex items-center gap-3 rounded-full bg-[#102036] px-8 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-white shadow-[0_12px_32px_rgb(16,32,54,0.30)] transition-all duration-300 hover:-translate-y-0.5"
              >
                Contact Kevin Ford
                <Mail className="h-4 w-4" />
              </a>
              <a
                href="tel:+16137626400"
                data-testid="ba-call-button"
                className="inline-flex items-center gap-3 rounded-full border border-[#102036]/20 bg-white px-8 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-[#102036] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#3682AE]"
              >
                613-762-6400
                <Phone className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="relative mx-auto max-w-md lg:max-w-none">
                <div className="absolute -inset-3 rounded-lg border border-[#3682AE]/25" aria-hidden="true" />
                <div className="relative overflow-hidden rounded-lg shadow-[0_30px_70px_rgb(16,32,54,0.18)]">
                  <img
                    src="/kevin-ford.jpg"
                    alt="Kevin Ford — Executive Advisor, Board Leader, former President & CEO of Calian Group Ltd."
                    className="h-[380px] w-full object-cover object-top lg:h-[460px]"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#102036]/90 via-[#102036]/40 to-transparent p-6 pt-14">
                    <p className="font-display text-2xl font-semibold text-white">Kevin Ford</p>
                    <p className="mt-1 text-[12px] font-semibold uppercase tracking-[0.16em] text-white/80">
                      Executive Advisor · Former President &amp; CEO, Calian Group Ltd.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative overflow-hidden bg-[#102036] py-14" data-testid="ba-stats">
        <div className="grain pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-[1320px] grid-cols-2 gap-8 px-5 sm:px-8 lg:grid-cols-4">
          {STATS.map(([value, label], i) => (
            <Reveal key={label} delay={0.06 * i}>
              <div>
                <p className="font-display text-4xl font-semibold text-white sm:text-5xl" data-testid={`ba-stat-${i}`}>{value}</p>
                <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#3682AE]">{label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* About the advisor */}
      <section className="bg-white py-24" data-testid="ba-about">
        <div className="mx-auto grid max-w-[1320px] gap-14 px-5 sm:px-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <Eyebrow>Your advisor</Eyebrow>
                <h2 className="mt-4 font-display text-4xl font-medium leading-tight tracking-tight text-[#102036] sm:text-5xl">
                  A proven operator's perspective.
                </h2>
                <p className="mt-6 max-w-md text-base leading-relaxed text-[#4A5568]">
                  Through Ford &amp; Associates, Kevin Ford brings senior-level business advisory support to
                  organizations seeking experienced leadership perspective, operational insight, and
                  boardroom-ready guidance.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-[17px] leading-relaxed text-[#102036]/85">
                Kevin Ford is a Canadian business leader best known for his transformative ten-year tenure as
                President and Chief Executive Officer of Calian Group Ltd., a diversified provider of
                mission-critical solutions in defence, space, healthcare, and infrastructure services.
              </p>
              <p className="mt-5 text-[17px] leading-relaxed text-[#102036]/85">
                Under Kevin's leadership, Calian evolved from a conservative, technology-focused firm into a
                dynamic global enterprise with diversified offerings and significant growth through organic
                initiatives, strategic acquisitions, and international expansion.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-12 text-[11px] font-bold uppercase tracking-[0.24em] text-[#4A5568]/70">Recognition</p>
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {RECOGNITION.map((r) => (
                  <li key={r} className="flex gap-3 rounded-md border border-[#102036]/10 bg-[#FFFFFF] p-4 text-[13px] leading-relaxed text-[#102036]/80 shadow-[0_2px_10px_rgb(16,32,54,0.04)]">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#3682AE]" />
                    {r}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Advisory services */}
      <section className="bg-[#FFFFFF] py-24" data-testid="ba-services">
        <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>How he helps</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-display text-4xl font-medium leading-tight tracking-tight text-[#102036] sm:text-5xl">
              Senior guidance, in three forms.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {ADVISORY_SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={0.07 * i}>
                <div className="flex h-full flex-col rounded-lg border border-[#102036]/10 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#3682AE]/40 hover:shadow-[0_16px_40px_rgb(16,32,54,0.08)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-[#3682AE]/10 text-[#3682AE]">
                    <s.icon className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-semibold text-[#102036]">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#4A5568]">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#FFFFFF] py-24" data-testid="ba-process">
        <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
          <Reveal>
            <Eyebrow>The engagement</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-display text-4xl font-medium leading-tight tracking-tight text-[#102036] sm:text-5xl">
              A clear path from challenge to action.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px overflow-hidden rounded-lg border border-[#102036]/10 bg-[#102036]/10 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.num} delay={0.06 * i} className="h-full">
                <div className="flex h-full flex-col bg-white p-8">
                  <span className="font-display text-3xl italic text-[#3682AE]">{p.num}</span>
                  <h3 className="mt-5 text-[15px] font-bold text-[#102036]">{p.title}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-[#4A5568]">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal for */}
      <section className="bg-[#FFFFFF] py-24" data-testid="ba-ideal-for">
        <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <Eyebrow>Who it's for</Eyebrow>
                <h2 className="mt-4 font-display text-4xl font-medium leading-tight tracking-tight text-[#102036] sm:text-5xl">
                  Built for the rooms where decisions happen.
                </h2>
              </div>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {IDEAL_FOR.map((c, i) => (
              <Reveal key={c.title} delay={0.07 * i}>
                <div className="h-full rounded-lg border border-[#102036]/10 bg-[#FFFFFF] p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgb(16,32,54,0.07)]">
                  <Compass className="h-5 w-5 text-[#3682AE]" strokeWidth={1.75} />
                  <h3 className="mt-5 font-display text-2xl font-semibold text-[#102036]">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#4A5568]">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-[#102036] py-20" data-testid="ba-cta">
        <div className="grain pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-[#3682AE]/25 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-2xl">
                <h2 className="font-display text-4xl font-medium leading-tight text-white sm:text-5xl">
                  Grow, transition, and lead with confidence.
                </h2>
                <p className="mt-4 text-[15px] leading-relaxed text-white/75">
                  Connect with Ford &amp; Associates to explore how Kevin Ford's executive advisory support can
                  help your organization move forward.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <a
                  href="mailto:kevin@fordassociates.ca?subject=Website%20Contact"
                  data-testid="ba-cta-email"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036] transition-all duration-300 hover:-translate-y-0.5"
                >
                  Email Kevin <Mail className="h-4 w-4" />
                </a>
                <a
                  href="tel:+16137626400"
                  data-testid="ba-cta-call"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-white"
                >
                  Call <Phone className="h-4 w-4" />
                </a>
                <a
                  href="https://www.linkedin.com/in/kevin-ford-5426948/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ba-cta-linkedin"
                  aria-label="Kevin Ford on LinkedIn (opens in a new tab)"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-white"
                >
                  LinkedIn <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/#contact"
              data-testid="ba-general-contact-link"
              className="mt-12 inline-flex items-center gap-2 border-t border-white/15 pt-6 text-[12px] font-bold uppercase tracking-[0.16em] text-white/70 transition-colors hover:text-white"
            >
              Or send the firm a general enquiry <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
